import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SCOTIA_COLOURS, SCOTIA_IMAGE } from "../config";

export const ScotiaPicker = ({ colours }) => {
  const [selected, setSelected] = useState(SCOTIA_COLOURS[0]);
  const byName = (n) => colours.find((c) => c.name === n && c.style !== "herringbone") || colours.find((c) => c.name === n);
  const image = (n) => byName(n)?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${byName(n).image_url}` : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-testid="scotia-section">
      <motion.div
        className="lg:col-span-6"
        initial={{ opacity: 0, y: 40, clipPath: "inset(12% round 28px)" }}
        whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% round 28px)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="relative aspect-square overflow-hidden rounded-[28px] border border-white/10 group" data-testid="scotia-visual">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${SCOTIA_IMAGE}`}
            alt="Colour-matched scotia profiles fanned out in five timber-look finishes"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
          <div className="absolute top-6 left-6 flex items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E8B57F]">Scotia</span>
            <span className="h-px w-8 bg-[#E8B57F]/60" />
            <span className="hidden sm:inline text-[10px] tracking-[0.2em] uppercase text-white/80">Colour-matched</span>
          </div>
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
            <p className="text-sm text-white/85 max-w-xs leading-relaxed">
              Timber-look face, quarter-round profile — fitted along the skirting to hide the expansion gap.
            </p>
            <div className="shrink-0 flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs text-white border border-white/15" data-testid="scotia-selected-label">
              {image(selected) && <img src={image(selected)} alt="" className="w-4 h-4 rounded-full object-cover ring-1 ring-white/40" />}
              {selected}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="lg:col-span-6">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8B57F]">01 — Scotia</p>
        <h3 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-white font-heading">
          Scotia that matches your floor, not just “close enough”
        </h3>
        <p className="mt-5 text-base text-white/70 leading-relaxed max-w-xl">
          Scotia is the small quarter-round moulding that covers the expansion gap where your new floor meets the skirting.
          Ours comes in a timber-look finish colour-matched to the hybrid range, so the edge of the room looks as neat as the middle.
        </p>

        <p className="mt-8 text-sm font-semibold text-white/90">Pick your flooring to match the scotia</p>
        <div className="mt-3 flex flex-wrap gap-2.5" data-testid="scotia-colour-list">
          {SCOTIA_COLOURS.map((n) => {
            const on = selected === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setSelected(n)}
                aria-pressed={on}
                data-testid={`scotia-colour-${n.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-[background-color,color,transform] duration-300 active:scale-95 border ${
                  on
                    ? "bg-[#C87A38] border-[#C87A38] text-white"
                    : "bg-white/[0.04] border-white/15 text-white/80 hover:bg-white/10 hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  {image(n) && <img src={image(n)} alt="" className="w-4 h-4 rounded-full object-cover ring-1 ring-white/30" />}
                  {n}
                </span>
              </button>
            );
          })}
        </div>

        <Link
          to="/colours"
          className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#E8B57F] hover:text-white transition-colors group"
          data-testid="scotia-browse-colours-link"
        >
          See the full colour range
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};
